import {
    Checkbox,
    createTheme,
    CSSVariablesResolver,
    Group,
    Modal,
    NumberInput,
    Paper,
    PasswordInput,
    RadioGroup,
    rem,
    Select,
    Table,
    Textarea,
    TextInput,
    FileInput,
} from '@mantine/core';
import { Property } from 'csstype';
import { DateInput, DateTimePicker } from '@mantine/dates';

export const AtMantineSizeArray = ['none', 'xxs', 'xs', 'sm', 'lg', 'xl', 'xxl', 'xxxl', 'xxxxl'];

export const theme = createTheme({
    fontFamily: 'Roboto, sans-serif',
    headings: {
        fontWeight: '700',
        sizes: {
            h1: {},
            h2: {
                fontSize: rem('32px'),
                lineHeight: rem('40px'),
            },
            h3: {
                fontSize: rem('24px'),
                lineHeight: rem('32px'),
            },
            h4: {
                fontSize: rem('20px'),
                lineHeight: rem('24px'),
            },
            h5: {},
            h6: {},
        },
    },
    // breakpoints: {
    //     xxs: em(390),
    //     xs: em(576),
    //     sm: em(768),
    //     md: em(992),
    //     lg: em(1200),
    //     xl: em(1408),
    // },
    spacing: {
        none: rem(0),
        xxs: rem(4),
        xs: rem(8),
        sm: rem(12),
        md: rem(16),
        lg: rem(24),
        xl: rem(32),
        xxl: rem(40),
        xxxl: rem(40),
        xxxxl: rem(64),
        xxxxxl: rem(80),
    },
    fontSizes: {
        xxl: rem(48),
    },
    defaultGradient: {
        from: 'var(--mantine-color-gray-2)',
        to: 'white',
        deg: 45,
    },
    components: {
        Paper: Paper.extend({
            vars: (theme) => ({
                root: {
                    padding: rem(theme.spacing.lg),
                    '--paper-radius': theme.radius.lg,
                    '--paper-shadow': theme.shadows.md,
                },
            }),
        }),
        Group: Group.extend({
            vars: () => ({
                root: { alignItems: 'start' },
            }),
        }),
        TextInput: TextInput.extend({
            defaultProps: {
                maxLength: 200,
            },
            vars: () => ({
                root: {},
                label: {
                    marginBottom: theme.spacing?.xxs,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: theme.lineHeights?.lg,
                },
                description: { fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        DateInput: DateInput.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { fontSize: 14 },
                input: { marginTop: theme.spacing?.xxs, fontSize: 16, fontWeight: 400 },
            }),
        }),
        DateTimePicker: DateTimePicker.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { fontSize: 14 },
                input: { marginTop: theme.spacing?.xxs, fontSize: 16, fontWeight: 400 },
            }),
        }),
        NumberInput: NumberInput.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { fontSize: 14 },
                input: { marginTop: theme.spacing?.xxs, fontSize: 16, fontWeight: 400 },
            }),
        }),
        Textarea: Textarea.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { fontSize: 14 },
                input: { marginTop: theme.spacing?.xxs, fontSize: 16, fontWeight: 400 },
            }),
        }),
        Tabs: Textarea.extend({
            vars: () => ({
                tab: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        Select: Select.extend({
            vars: () => ({
                root: {},
                label: {
                    marginBottom: theme.spacing?.xxs,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: theme.lineHeights?.lg,
                },
                description: { fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        RadioGroup: RadioGroup.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { marginBottom: theme.spacing?.xxs, fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        Checkbox: Checkbox.extend({
            vars: () => ({
                root: {},
                label: { fontSize: 16, fontWeight: 600, lineHeight: theme.lineHeights?.lg },
                description: { marginBottom: theme.spacing?.xxs, fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        PasswordInput: PasswordInput.extend({
            vars: () => ({
                root: {},
                label: {
                    marginTop: theme.spacing?.xxs,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: theme.lineHeights?.lg,
                },
                description: { fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
        Modal: Modal.extend({
            vars: () => ({
                root: {},
                content: { borderRadius: 8, padding: theme.spacing?.xs },
                title: { fontWeight: 700, fontSize: 20 },
            }),
        }),
        Table: Table.extend({
            vars: () => ({
                td: {
                    whiteSpace: 'nowrap',
                    alignContent: 'center',
                },
                th: {
                    whiteSpace: 'nowrap',
                    alignContent: 'center',
                },
            }),
        }),
        FileInput: FileInput.extend({
            vars: (theme) => ({
                root: {},
                label: {
                    marginBottom: theme.spacing?.xxs,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: theme.lineHeights?.lg,
                },
                description: { fontSize: 14 },
                input: { fontSize: 16, fontWeight: 400 },
            }),
        }),
    },
    other: {},
});

export const cssVariablesResolver: CSSVariablesResolver = () => ({
    variables: {
        '--mantine-spacing-none': rem(0),
        '--mantine-spacing-2xs': rem(4),
        '--mantine-spacing-xs': rem(8),
        '--mantine-spacing-sm': rem(12),
        '--mantine-spacing-md': rem(16),
        '--mantine-spacing-lg': rem(24),
        '--mantine-spacing-xl': rem(32),
        '--mantine-spacing-xxl': rem(40),
        '--mantine-spacing-2xl': rem(40),
        '--mantine-spacing-3xl': rem(64),
        '--mantine-spacing-4xl': rem(80),
    },
    dark: {},
    light: {},
});

export const mix = (
    color1: Property.Color, // Utilisation de Property.Color
    color2: Property.Color, // Utilisation de Property.Color
    weight: number = 50,
): string => {
    const d2h = (d: number) => d.toString(16);
    const h2d = (h: string) => parseInt(h, 16);

    // Enlever le '#' des couleurs hexadécimales
    color1 = color1.replace('#', '');
    color2 = color2.replace('#', '');

    let color = '#';
    for (let i = 0; i <= 5; i += 2) {
        const v1 = h2d(color1.substr(i, 2));
        const v2 = h2d(color2.substr(i, 2));
        let val = d2h(Math.round(v2 + (v1 - v2) * (weight / 100.0)));
        while (val.length < 2) val = '0' + val;
        color += val;
    }

    return color.toUpperCase();
};

export const tint = (color: Property.Color, weight: number = 50): string => mix('#FFFFFF', color, weight);
